import React from "react";
import { Box, Typography, Button, Grid, Paper } from "@mui/material";
import AddIcon from "@mui/icons-material/AddCircleOutline";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import StarsIcon from "@mui/icons-material/Stars";
import LoyaltyIcon from "@mui/icons-material/Loyalty";
import { Icons } from "../../assets/icon";

// —— Netflix-ish tokens (visual only)
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  text: "#ffffff",
  muted: "rgba(255,255,255,0.75)",
  border: "rgba(255,255,255,0.12)",
  red: "#E50914",
  redHover: "#b20710",
};

interface UnifiedWalletHeaderProps {
  totalValueUSD: number;
  purchasedChips: number;
  earnedChips: number;
  bonusChips: number;
  expiringBonus?: number;
  onPurchaseClick: () => void;
  onRedeemClick: () => void;
}

const UnifiedWalletHeader: React.FC<UnifiedWalletHeaderProps> = ({
  totalValueUSD,
  purchasedChips,
  earnedChips,
  bonusChips,
  expiringBonus = 0,
  onPurchaseClick,
  onRedeemClick,
}) => {
  const totalChips = purchasedChips + earnedChips;

  const cardData = [
    {
      label: "PCHIPS",
      value: purchasedChips,
      desc: "Purchased chips, usable across various activities.",
      icon: <Icons.PCoin height={25} width={25} />,
      gradient: "linear-gradient(145deg,#f8d442,#ffd700)",
      bg: "rgba(248,212,66,0.15)",
      glow: "rgba(255,224,102,0.3)",
    },
    {
      label: "RCHIPS",
      value: earnedChips,
      desc: "Earned from sessions, redeemable for cash.",
      icon: <Icons.RCoin height={25} width={25} />,
      gradient: "linear-gradient(145deg,#34d399,#10b981)",
      bg: "rgba(52,211,153,0.15)",
      glow: "rgba(110,231,183,0.3)",
    },
    {
      label: "BCHIPS",
      value: bonusChips,
      desc:
        expiringBonus > 0
          ? `Out of total ${bonusChips} bonus chips, ${expiringBonus} is expiring soon.`
          : "Bonus chips. Not redeemable.",
      icon: <Icons.BCoin height={25} width={25} />,
      gradient: "linear-gradient(145deg,#f472b6,#ec4899)",
      bg: "rgba(244,114,182,0.15)",
      glow: "rgba(249,168,212,0.3)",
    },
  ];

  return (
    <Box
      maxWidth="xl"
      mx="auto"
      px={{ xs: 2, sm: 3, md: 6 }}
      mt={{ xs: 2, md: 4 }}
      mb={4}
      fontFamily="'Lexend', sans-serif"
    >
      {/* Header Hero */}
      <Box
        sx={{
          background: `linear-gradient(120deg,${COLORS.panel},rgba(99,102,241,0.3))`,
          backgroundSize: "200% 200%",
          animation: "gradientShift 6s ease infinite",
          color: COLORS.text,
          borderRadius: 6,
          p: { xs: 3, md: 6 },
          mb: { xs: 4, md: 6 },
          textAlign: "center",
          border: `1px solid ${COLORS.border}`,
          boxShadow: "0 16px 48px rgba(0,0,0,0.5)",
        }}
      >
        <Typography
          variant="h3"
          fontWeight={700}
          sx={{
            fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
            mb: 1,
          }}
        >
          Your Wallet Overview
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: "1rem", sm: "1.1rem" },
            opacity: 0.9,
            maxWidth: 600,
            mx: "auto",
            mb: 4,
          }}
        >
          Monitor, manage, and redeem your chips anytime.
        </Typography>

        <Typography
          sx={{
            fontWeight: 900,
            fontSize: { xs: "2.5rem", sm: "3.2rem", md: "4rem" },
            letterSpacing: "-1px",
            background: `linear-gradient(to right, ${COLORS.text}, ${COLORS.muted})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            mb: 1,
          }}
        >
          ${totalValueUSD.toFixed(2)}
        </Typography>
        <Typography sx={{ opacity: 0.9 }}>
          Total Wallet Value (PCHIPS + RCHIPS)
        </Typography>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: { xs: "1.2rem", sm: "1.4rem", md: "1.6rem" },
            mt: 1,
            color: COLORS.muted,
          }}
        >
          Total Chips: {totalChips}
        </Typography>
      </Box>

      {/* Wallet Cards */}
      <Grid container spacing={4}>
        {cardData.map((card, index) => (
          <Grid item xs={12} sm={4} key={index} minHeight={200}>
            <Paper
              elevation={0}
              sx={{
                position: "relative",
                background: COLORS.panel,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 5,
                p: 3,
                height: "100%",
                boxShadow: `0 8px 20px ${card.glow}`,
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: `0 12px 30px ${card.glow}`,
                  minHeight: 180,
                  borderColor: card.glow,
                },
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                mb={1.5}
                gap={1}
              >
                {card.icon}
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    color: COLORS.text,
                  }}
                >
                  {card.label}
                </Typography>
              </Box>
              <Typography
                variant="h4"
                fontWeight={900}
                textAlign="center"
                sx={{
                  background: card.gradient,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  mb: 1,
                }}
              >
                {card.value}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.95rem",
                  color: COLORS.muted,
                  textAlign: "center",
                }}
              >
                {card.desc}
              </Typography>
              {card.label !== "BCHIPS" && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 16,
                    right: 24,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    backgroundColor: "rgba(255,255,255,0.1)",
                    color: COLORS.text,
                    px: 1.2,
                    py: 0.4,
                    borderRadius: "10px",
                    border: `1px solid ${COLORS.border}`,
                    boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                  }}
                >
                  ${(card.value * 0.1).toFixed(2)}
                </Box>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Buttons */}
      <Box
        mt={6}
        display="flex"
        justifyContent="center"
        gap={2}
        flexWrap="wrap"
      >
        <Button
          variant="contained"
          onClick={onPurchaseClick}
          startIcon={<AddIcon />}
          sx={{
            background: COLORS.red,
            color: COLORS.text,
            borderRadius: "50px",
            px: 4,
            py: 1.5,
            fontWeight: 600,
            fontSize: "1rem",
            "&:hover": {
              background: COLORS.redHover,
            },
          }}
        >
          Purchase Chips
        </Button>
        <Button
          variant="contained"
          onClick={onRedeemClick}
          startIcon={<LoyaltyIcon />}
          sx={{
            background: "linear-gradient(90deg,#10b981,#059669)",
            color: COLORS.text,
            borderRadius: "50px",
            px: 4,
            py: 1.5,
            fontWeight: 600,
            fontSize: "1rem",
            "&:hover": {
              background: "linear-gradient(90deg,#059669,#047857)",
            },
          }}
        >
          Redeem Chips
        </Button>
      </Box>

      {/* Gradient animation */}
      <style>{`
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </Box>
  );
};

export default UnifiedWalletHeader;
